// utils/quoteValidation.js
import { isAfter, isBefore, isValid } from 'date-fns';

// Validation rules for quote form fields
export const validationSchema = {
    customerid: (value) => {
        if (!value || value === '') {
            return 'Kund måste väljas';
        }
        return null;
    },

    customer: (value) => {
        if (!value || !value.id) {
            return 'Kund måste väljas';
        }
        return null;
    },

    quoteNumber: (value) => {
        // Quote number is optional, generated by backend if not provided
        return null;
    },

    quoteDate: (value) => {
        if (!value) {
            return 'Offertdatum krävs';
        }
        if (!isValid(value)) {
            return 'Ogiltigt datum';
        }
        return null;
    },

    validUntil: (value, state) => {
        if (!value) {
            return 'Giltighetsdatum krävs';
        }
        if (!isValid(value)) {
            return 'Ogiltigt datum';
        }
        if (state.quoteDate && isBefore(value, state.quoteDate)) {
            return 'Giltighetsdatum måste vara efter offertdatum';
        }
        return null;
    },

    items: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
            return 'Minst en artikel krävs';
        }

        // Validate each item
        for (let i = 0; i < value.length; i++) {
            const item = value[i];

            if (!item.description || item.description.trim() === '') {
                return `Artikel ${i + 1}: Beskrivning krävs`;
            }

            if (item.quantity <= 0) {
                return `Artikel ${i + 1}: Antal måste vara större än 0`;
            }

            if (item.unitprice < 0) {
                return `Artikel ${i + 1}: Pris kan inte vara negativt`;
            }

            if (item.discount < 0 || item.discount > 100) {
                return `Artikel ${i + 1}: Rabatt måste vara mellan 0% och 100%`;
            }
        }

        return null;
    },

    deliveryOptions: (value, state) => {
        if (!value.method) {
            return 'Leveransmetod måste väljas';
        }

        if (value.method === 'email' && !value.email) {
            return 'E-postadress krävs för e-postleverans';
        }

        if (value.method === 'email' && value.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value.email)) {
                return 'Ogiltig e-postadress';
            }
        }

        if (value.method === 'sms' && !value.phone) {
            return 'Telefonnummer krävs för SMS-leverans';
        }

        if (value.method === 'sms' && value.phone) {
            // Basic phone validation (Swedish format)
            const phoneRegex = /^(\+46|0)[0-9]{9,10}$/;
            if (!phoneRegex.test(value.phone.replace(/\s/g, ''))) {
                return 'Ogiltigt telefonnummer';
            }
        }

        return null;
    },

    paymentMethods: (value) => {
        const hasSelectedMethod = Object.values(value || {}).some(v => v === true);
        if (!hasSelectedMethod) {
            return 'Minst en betalningsmetod måste väljas';
        }
        return null;
    },

    currency: (value) => {
        const validCurrencies = ['SEK', 'EUR', 'USD', 'NOK', 'DKK', 'GBP'];
        if (!value || !validCurrencies.includes(value)) {
            return 'Ogiltig valuta';
        }
        return null;
    },

    notes: (value) => {
        // Notes are optional but have a max length
        if (value && value.length > 2000) {
            return 'Anteckningar får inte överstiga 2000 tecken';
        }
        return null;
    }
};

// Validate a single field
export const validateField = (fieldName, value, state = {}) => {
    if (validationSchema[fieldName]) {
        return validationSchema[fieldName](value, state);
    }
    return null;
};

// Validate entire form
export const validateForm = (state) => {
    const errors = {};

    Object.keys(validationSchema).forEach(fieldName => {
        const error = validateField(fieldName, state[fieldName], state);
        if (error) {
            errors[fieldName] = error;
        }
    });

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Get validation state for Bootstrap form controls
export const getValidationState = (fieldName, errors, touched = {}) => {
    if (!touched[fieldName]) {
        return {};
    }

    const hasError = errors[fieldName];

    return {
        isInvalid: !!hasError,
        isValid: !hasError
    };
};

// Custom validation rules that can be added dynamically
export const customValidators = {
    // Validate Swedish organization number
    organizationNumber: (value) => {
        if (!value) return null;

        const cleaned = value.replace(/[^0-9]/g, '');
        if (cleaned.length !== 10 && cleaned.length !== 12) {
            return 'Organisationsnummer måste vara 10 eller 12 siffror';
        }

        // Simple modulus 10 check (Luhn algorithm)
        // This is a simplified version
        return null;
    },

    // Validate IBAN
    iban: (value) => {
        if (!value) return null;

        const cleaned = value.replace(/\s/g, '').toUpperCase();
        if (cleaned.length < 15 || cleaned.length > 34) {
            return 'IBAN måste vara mellan 15 och 34 tecken';
        }

        // More complex IBAN validation could be added here
        return null;
    },

    // Validate future date
    futureDate: (value) => {
        if (!value) return null;

        if (isBefore(value, new Date())) {
            return 'Datum måste vara i framtiden';
        }

        return null;
    }
};